import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_API_URL || 'https://web-production-120c.up.railway.app';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('shaheen_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

const DATA_CATEGORIES = [
  {
    key: 'conversations',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
      </svg>
    ),
    label: 'Chat history',
    description: 'All messages between you and your concierge',
    endpoint: (id) => `/client/${id}/data/conversations`,
    warning: 'Your entire conversation history will be erased. Your concierge will no longer have context from previous sessions.',
  },
  {
    key: 'documents',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
    ),
    label: 'Vault documents',
    description: 'Briefings, breach reports, assessment reports, and travel advisories',
    endpoint: (id) => `/client/${id}/data/documents`,
    warning: 'All documents in your vault will be permanently deleted, including briefings, breach reports, and assessment results.',
  },
  {
    key: 'scans',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
      </svg>
    ),
    label: 'Assessment history',
    description: 'Results from all your digital exposure assessments',
    endpoint: (id) => `/client/${id}/data/scans`,
    warning: 'All digital exposure assessment results will be deleted. Your protection score will reset on the next assessment.',
  },
  {
    key: 'risk_scores',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
    label: 'Score timeline',
    description: 'Your protection score history and trend chart',
    endpoint: (id) => `/client/${id}/data/risk-scores`,
    warning: 'Your protection score history will be cleared. The timeline chart will be empty until new assessments run.',
  },
];

function ConfirmModal({ category, onConfirm, onCancel, loading }) {
  return (
    <div
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 14, padding: 24, width: '100%', maxWidth: 400 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--red)', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
            Delete {category.label.toLowerCase()}?
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
          {category.warning}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{ flex: 1, padding: '10px', background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            Go back
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{ flex: 1, padding: '10px', background: 'var(--red)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Deleting…' : 'Yes, delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Privacy() {
  const { client } = useAuth();
  const clientId = client?.id;

  const [confirming, setConfirming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [done, setDone] = useState({});
  const [error, setError] = useState('');

  const handleDelete = async (category) => {
    setDeleting(category.key);
    setError('');
    try {
      const res = await authFetch(`${API}${category.endpoint(clientId)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed');
      setDone(prev => ({ ...prev, [category.key]: true }));
    } catch {
      setError('Something went wrong. Please try again or contact your concierge.');
    } finally {
      setDeleting(null);
      setConfirming(null);
    }
  };

  const activeCategory = DATA_CATEGORIES.find(c => c.key === confirming);

  return (
    <div style={{ padding: '20px 16px', overflowY: 'auto', flex: 1 }}>
      {activeCategory && (
        <ConfirmModal
          category={activeCategory}
          loading={deleting === activeCategory.key}
          onConfirm={() => handleDelete(activeCategory)}
          onCancel={() => setConfirming(null)}
        />
      )}

      <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>My Data</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
        You control what we keep. Delete any category below — your active protection and monitoring are not affected.
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '0.5px solid var(--red)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {DATA_CATEGORIES.map(cat => {
          const isDone = done[cat.key];
          return (
            <div
              key={cat.key}
              style={{ background: 'var(--bg-surface)', border: `0.5px solid ${isDone ? 'var(--green)' : 'var(--border)'}`, borderRadius: 10, padding: '16px', display: 'flex', alignItems: 'center', gap: 14 }}
            >
              <div style={{ width: 40, height: 40, borderRadius: 8, background: isDone ? 'rgba(34,197,94,0.1)' : 'var(--bg-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDone ? 'var(--green)' : 'var(--text-muted)', flexShrink: 0 }}>
                {isDone ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : cat.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: isDone ? 'var(--text-muted)' : 'var(--text-primary)', marginBottom: 2 }}>
                  {cat.label}
                  {isDone && <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--green)', marginLeft: 8 }}>Deleted</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{cat.description}</div>
              </div>
              {!isDone && (
                <button
                  onClick={() => setConfirming(cat.key)}
                  style={{ fontSize: 11, padding: '6px 14px', background: 'transparent', border: '0.5px solid var(--red)', borderRadius: 7, color: 'var(--red)', cursor: 'pointer', flexShrink: 0, opacity: 0.85 }}
                >
                  Delete
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 28, background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px' }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 8 }}>What is not deleted</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
          Deleting data here does not affect your active monitoring, breach alerts, or protection status.
          Your subscription, watchlist, and monitoring targets remain active.
          If you want to close your account entirely, contact your concierge.
        </div>
      </div>
    </div>
  );
}
